import { inject, Injectable } from '@angular/core';

import { captureMessage } from '@sentry/angular';
import { defer, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import assert from 'tiny-invariant';

import { BormaDagoCsrfApiConsumerService } from '@ts/shared/api/data-access-borma-dago-csrf';
import {
  BormaDagoErrorCode,
  BormaDagoHttpErrorResponse,
} from '@ts/shared/api/util-borma-dago';
import {
  AuthenticationAudienceToken,
  AuthenticationJwtState,
} from '@ts/shared/authentication/util-core';

import { AuthJwtTokenStore } from './auth-jwt-token-store';

/**
 * Maps an HttpErrorResponse status code to a coarse failure category we
 * use as a Sentry tag. Exported for tests so we can lock the contract
 * between status codes and metric labels.
 *
 * - `network_error`: request never reached the server (Angular reports
 *   status 0 for network/CORS/aborted; undefined for non-HTTP errors).
 * - `server_error`: 5xx from the backend.
 * - `auth_error`: 401/403 — the token was definitively rejected.
 * - `other`: anything else (4xx that isn't auth, etc.).
 */
export function classifyJwtRenewalFailureKind(
  status: number | undefined,
): 'network_error' | 'server_error' | 'auth_error' | 'other' {
  if (status === undefined || status === 0) {
    return 'network_error';
  }
  if (status >= 500) {
    return 'server_error';
  }
  if (status === 401 || status === 403) {
    return 'auth_error';
  }
  return 'other';
}

/**
 * Handles JWT token renewal.  Has no dependency on AuthenticationService,
 * so it can be injected by API consumers without closing the old DI cycle.
 *
 * When the server definitively rejects the credential, emits on
 * {@link authenticationRejected$}; AuthenticationService subscribes to that
 * stream in its constructor and calls logout().
 */
@Injectable({ providedIn: 'root' })
export class AuthJwtRenewalService {
  private csrfApiConsumer = inject(BormaDagoCsrfApiConsumerService);
  private tokenStore = inject(AuthJwtTokenStore);

  /** Mutable to preserve existing test pattern (`service.jwtTokenUrl = '...'`). */
  jwtTokenUrl = 'api/jwt/token/';

  readonly JWT_ERROR_CODES_TO_HANDLE_MANUALLY: readonly BormaDagoErrorCode[] = [
    'permission_denied',
  ];

  private readonly authenticationRejectedSubject =
    new Subject<BormaDagoHttpErrorResponse>();

  /**
   * Emits only when the server has definitively rejected the credential
   * (401/403) — never for a failure the server did not make.
   *
   * Inversion-of-control hook: AuthenticationService subscribes once at
   * construction and triggers logout().  Keeps this service free of any
   * AuthenticationService import → no DI cycle.
   */
  readonly authenticationRejected$: Observable<BormaDagoHttpErrorResponse> =
    this.authenticationRejectedSubject.asObservable();

  renew$(): Observable<AuthenticationJwtState> {
    return this.csrfApiConsumer
      .post$<AuthenticationJwtState>({
        relativeUrl: this.jwtTokenUrl,
        // all information needed should already be in the cookies
        body: {},
        errorCodesToHandleManually: this.JWT_ERROR_CODES_TO_HANDLE_MANUALLY,
      })
      .pipe(
        catchError((error: BormaDagoHttpErrorResponse) => {
          // Telemetry must never block the safety-critical logout path.
          try {
            this.captureJwtRenewalFailure(error);
          } catch {
            // intentionally swallow
          }
          // Only a rejection the server actually made may end the session:
          // logout() deletes COOKIE_IS_AUTHENTICATED, the sole trigger for a
          // restore on the next boot, so an offline start would otherwise
          // destroy a session the server still honours for 90 days (#10903).
          if (classifyJwtRenewalFailureKind(error.status) === 'auth_error') {
            this.authenticationRejectedSubject.next(error);
          }
          return EMPTY;
        }),
        tap((state) => {
          this.tokenStore.setState(state);
        }),
      );
  }

  /**
   * Mints a token for another service from the session cookies and stores it
   * under its audience.
   *
   * A refusal is never a reason to end the staff session, so unlike
   * {@link renew$} it never reaches {@link authenticationRejected$}: the
   * consumer's default path toasts it and completes without a token.
   *
   * `relativeUrl` must be solvent's: the POST carries the session cookie and
   * the csrf token, and an absolute URL would send them to its own host.
   */
  renewAudienceToken$({
    audience,
    relativeUrl,
    scope,
  }: {
    audience: string;
    relativeUrl: string;
    scope: string;
  }): Observable<AuthenticationAudienceToken> {
    assert(
      !/^[a-z][a-z\d+.-]*:|^\/\//i.test(relativeUrl),
      `renewAudienceToken$ needs a relative URL, got ${relativeUrl}`,
    );
    return defer(() => {
      const sessionGeneration = this.tokenStore.sessionGeneration;
      return this.csrfApiConsumer
        .post$<AuthenticationAudienceToken>({ relativeUrl, body: { scope } })
        .pipe(
          tap((token) => {
            // A logout while the POST was in flight must not leave this
            // session's ticket behind for the next user in the tab.
            if (this.tokenStore.sessionGeneration === sessionGeneration) {
              this.tokenStore.setAudienceToken(audience, token);
            }
          }),
        );
    });
  }

  /**
   * Reports every failed JWT renewal to Sentry — including the kinds that
   * no longer end the session — so the split between network/server
   * failures and definitive auth failures stays measurable.
   */
  private captureJwtRenewalFailure(error: BormaDagoHttpErrorResponse): void {
    const status = error.status;
    const errorCode = error.error.error_code ?? 'unknown';
    captureMessage('jwt_renewal_failed', {
      level: 'warning',
      tags: {
        jwt_renewal_failure_kind: classifyJwtRenewalFailureKind(status),
        jwt_renewal_status: String(status),
        jwt_renewal_error_code: errorCode,
      },
      extra: {
        jwtTokenUrl: this.jwtTokenUrl,
        error_name: error.name,
        error_message: error.message,
      },
    });
  }
}
